import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

//helpers
import Moment from 'react-moment';
import apiClient from "../../Api";
import LoadSpinner from "../Shared/Common-UI/LoadSpinner";
import SimpleModal from "../Shared/SimpleModal";
import YesNoModal from "../Shared/YesNoModal";
import JsonToCSVDownload from "../Utility/Utility";
import FormularyUploadProgress from "../Shared/FormularyUploadProgress";


function FormularyAdd() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const modalProp = {content:[],isError:false,size:'sm'};
  const [showModal, setShowModal] = useState(false);
  const [modalMsg, setModalMsg] = useState(modalProp);
  const [prevStatus,setPrevStatus]= useState(true);
  const [showWarningModal, setShowWarningModal]=useState(false);
  const [activeInactiveWarningMsg,setActiveInactiveWarningMsg]= useState('');
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const progressTimerRef = useRef(null);
  const progressPollRef = useRef(null);
  const progressHideTimeoutRef = useRef(null);
  // Store active upload in memory for cross-checking
  const activeUploadRef = useRef(null);
  const status = [
    { key: "3", value: "Excluded" },
    { key: "-1", value: "No Formulary" },
    { key: "10", value: "Not On Formulary" },
    { key: "5", value: "Not Reimbursable" },
    { key: "30", value: "On Formulary" },
    { key: "60", value: "Preferred" },
    {
      key: "22",
      value: "Prior Authorization May Be Required",
    },
    {
      key: "20",
      value: "Prior Authorization Required",
    },
    { key: "11", value: "Restricted" },
  ];
  const [formularyStatus] = useState(status);

  const [formulary, setFormulary] = useState({
    id:0,
    description: "",
    defaultGenericFormularyStatus: "",
    defaultBrandFormularyStatus: "",
    defaultOTCFormularyStatus: "",
    file: "",
    isActive: true,
  });
  const [formError, setFormError] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        setLoading(true);
        await apiClient().get(`/api/formularies/${id}`).then(
          (response) => {
            setFormulary(response.data);
            setPrevStatus(response.data.isActive);
            setLoading(false);
          },
          (err) => {
            setLoading(false);
          }
        );
      }
    }
    fetchData();
  }, [id]);

  const stopProgressPolling = useCallback(() => {
    if (progressPollRef.current) {
      clearInterval(progressPollRef.current);
      progressPollRef.current = null;
    }
    if (progressHideTimeoutRef.current) {
      clearTimeout(progressHideTimeoutRef.current);
      progressHideTimeoutRef.current = null;
    }
    activeUploadRef.current = null;
  }, []);

  const startProgressPolling = useCallback((uploadId) => {
    activeUploadRef.current = {
      uploadId: uploadId,
      formularyId: id || null,
      startedAt: new Date().toISOString()
    };

    progressPollRef.current = setInterval(async () => {
      try {
        const response = await apiClient().get(`/api/formularies/upload-progress/${uploadId}`);
        setUploadProgress(response.data);

        if (response.data.status === 'Completed' || response.data.status === 'Failed') {
          stopProgressPolling();
          localStorage.removeItem('activeUpload');
          setLoading(false);

          progressHideTimeoutRef.current = setTimeout(() => {
            setShowProgressModal(false);
            setUploadProgress(null);

            const modalProp = {content:[], isError:false, size:'sm'};
            if (response.data.status === 'Completed') {
              modalProp.content = [];
              modalProp.content.push(`Formulary ${id ? 'updated' : 'added'} successfully`);
              modalProp.isError = false;
              setModalMsg(modalProp);
              setShowModal(true);
            } else if (response.data.status === 'Failed') {
              modalProp.content = [];
              modalProp.content.push(response.data.errorMessage || 'Upload failed');
              modalProp.isError = true;
              modalProp.size = 'lg';
              setModalMsg(modalProp);
              setShowModal(true);
            }
          }, 2000);
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          // 404 means progress not found - this is expected during initial upload phase
          // Continue polling as the progress entry might not be created yet
        } else if (error.response) {
          console.error('Error fetching upload progress:', error);
        }
      }
    }, 1500);
  }, [id, stopProgressPolling]);

  useEffect(() => {
    const checkActiveUpload = async () => {
      if (activeUploadRef.current) {
        return;
      }

      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return;
      }

      let backendUploads = [];
      let backendUpload = null;

      try {
        const response = await apiClient().get('/api/formularies/upload-progress/active');
        backendUploads = response.data || [];

        if (backendUploads.length > 0) {
          backendUpload = backendUploads[0];
        }
      } catch (error) {
        console.error('Failed to check backend for active uploads:', error);
        return;
      }

      const activeUploadStr = localStorage.getItem('activeUpload');
      if (activeUploadStr) {
        try {
          const localUpload = JSON.parse(activeUploadStr);

          if (backendUpload) {
            if (localUpload.uploadId !== backendUpload.uploadId) {
              localStorage.setItem('activeUpload', JSON.stringify({
                uploadId: backendUpload.uploadId,
                formularyId: backendUpload.formularyId,
                startedAt: backendUpload.startedAt
              }));
            }
          } else {
            localStorage.removeItem('activeUpload');
          }
        } catch (error) {
          console.error('Failed to parse activeUpload from localStorage:', error);
          localStorage.removeItem('activeUpload');
        }
      } else if (backendUpload) {
        localStorage.setItem('activeUpload', JSON.stringify({
          uploadId: backendUpload.uploadId,
          formularyId: backendUpload.formularyId,
          startedAt: backendUpload.startedAt
        }));
      }

      if (backendUpload) {
        const uploadIdToCheck = backendUpload.uploadId;
        const formularyIdForUpload = backendUpload.formularyId;
        const startedAt = new Date(backendUpload.startedAt);

        if (formularyIdForUpload) {
          if ((id && formularyIdForUpload.toString() !== id) || !id) {
            navigate(`/formularies/edit/${formularyIdForUpload}`);
            return;
          }
        }

        activeUploadRef.current = {
          uploadId: uploadIdToCheck,
          formularyId: formularyIdForUpload,
          startedAt: startedAt ? startedAt.toISOString() : new Date().toISOString()
        };

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(null);

        const checkProgressWithRetry = async (retryCount = 0, maxRetries = 3) => {
          try {
            const response = await apiClient().get(`/api/formularies/upload-progress/${uploadIdToCheck}`);

            if (response.data && response.data.status === 'InProgress') {
              setUploadProgress(response.data);
              startProgressPolling(uploadIdToCheck);
            } else if (response.data && (response.data.status === 'Completed' || response.data.status === 'Failed')) {
              localStorage.removeItem('activeUpload');
              setShowProgressModal(false);
              setLoading(false);
            }
          } catch (error) {
            if (error.response && error.response.status === 404) {
              const secondsSinceStart = startedAt ? (Date.now() - startedAt) / 1000 : 999;

              if (secondsSinceStart < 30 && retryCount < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, 2000));
                await checkProgressWithRetry(retryCount + 1, maxRetries);
              } else {
                localStorage.removeItem('activeUpload');
                setShowProgressModal(false);
                setLoading(false);
              }
            } else {
              localStorage.removeItem('activeUpload');
              setShowProgressModal(false);
              setLoading(false);
            }
          }
        };

        await checkProgressWithRetry();
      }
    };

    checkActiveUpload();

    return () => {
      if (progressPollRef.current) {
        clearInterval(progressPollRef.current);
        progressPollRef.current = null;
      }
      if (progressTimerRef.current) {
        clearTimeout(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      if (progressHideTimeoutRef.current) {
        clearTimeout(progressHideTimeoutRef.current);
        progressHideTimeoutRef.current = null;
      }
    };
  }, [id, navigate, startProgressPolling]);

  const patten = /\s/g;

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormulary({ ...formulary, [name]: value });
    setFormError({
      ...formError,
      [name]: value ? "" : "This field is required",
    });
  };

  const onKeyUp = (event) => {
    const { name, value } = event.target;
    var isSpace = patten.test(event.target.value[0])
    if (isSpace) {
      setFormError({
        ...formError,
        [name]: value === isSpace ? "" : "Description cannot begin with a space",
      });
    }
  }

  const changeHandler = (e) => {
    setFormulary((prevState) => ({
      ...prevState,
      file: e.target.files[0],
    }));
    setFormError((prevState) => ({
      ...prevState,
      file: "",
    }));
  };

  const generateUploadId = () => {
    const cryptoObj = window.crypto || window.msCrypto;
    if (cryptoObj && cryptoObj.getRandomValues) {
      const array = new Uint32Array(2);
      cryptoObj.getRandomValues(array);
      const randomStr = array[0].toString(36) + array[1].toString(36);
      return 'upload_' + Date.now() + '_' + randomStr;
    }
    return 'upload_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);
  };

  useEffect(() => {
    return () => {
      if (progressTimerRef.current) {
        clearTimeout(progressTimerRef.current);
      }
      if (progressPollRef.current) {
        clearInterval(progressPollRef.current);
      }
      if (progressHideTimeoutRef.current) {
        clearTimeout(progressHideTimeoutRef.current);
      }
    };
  }, []);

  const formularyAdd = async () => {
    // Prevent duplicate uploads - check if there's already an active upload
    if (activeUploadRef.current) {
      const modalProp = {content:[], isError:true, size:'sm'};
      modalProp.content.push('An upload is already in progress. Please wait for it to complete.');
      setModalMsg(modalProp);
      setShowModal(true);
      return false;
    }

    let formError = formularyValidation(formulary);
    if (!formError.isFormValid) {
      setFormError(formError);
      return false;
    }

    delete formulary.cancelDate;
    var formData = new FormData();
    for ( var key in formulary ) {
      formData.append(key, formulary[key]);
    }

    // Generate upload ID for progress tracking
    const uploadId = generateUploadId();

    // Store in memory immediately to prevent duplicate uploads
    activeUploadRef.current = {
      uploadId: uploadId,
      formularyId: id || null,
      startedAt: new Date().toISOString()
    };

    // Persist uploadId to localStorage for recovery after page refresh
    localStorage.setItem('activeUpload', JSON.stringify({
      uploadId: uploadId,
      formularyId: id || null,
      startedAt: new Date().toISOString()
    }));

    const config = { headers: { "content-type": "multipart/form-data" } };
    const method = id? 'put':'post';
    const url=`/api/formularies?uploadId=${uploadId}`;
    setLoading(true);

    // Start timer to show progress modal after 7 seconds
    progressTimerRef.current = setTimeout(() => {
      setShowProgressModal(true);
      startProgressPolling(uploadId);
    }, 7000);

    await apiClient()({ method: method, url: url, data: formData,config:config}).then(
      (response) => {
        // Clear the 7-second timer if upload completes quickly
        if (progressTimerRef.current) {
          clearTimeout(progressTimerRef.current);
          progressTimerRef.current = null;
        }

        // Stop polling if it was started
        stopProgressPolling();

        // Clean up localStorage on quick completion
        localStorage.removeItem('activeUpload');

        // Hide progress modal if it was shown
        setShowProgressModal(false);
        setUploadProgress(null);

        setLoading(false);
        let msg= `Formulary ${response.status === 200? 'updated':'added'} successfully`;
        modalProp.content = [];
        modalProp.content.push(msg);
        modalProp.isError=false;
        setModalMsg(modalProp)
        setShowModal(true);
      },(err)=>{
        // Clear the 7-second timer on error
        if (progressTimerRef.current) {
          clearTimeout(progressTimerRef.current);
          progressTimerRef.current = null;
        }

        // Stop polling if it was started
        stopProgressPolling();

        // Clean up localStorage on error
        localStorage.removeItem('activeUpload');

        // Hide progress modal if it was shown
        setShowProgressModal(false);
        setUploadProgress(null);

        let errMsg=[];
        if(err.response.statusText === "Unauthorized"){
          errMsg.push("Unauthorized User");
        }
        else if(err?.response?.data?.errors){
          let errors =err?.response?.data?.errors;
          errMsg = [];
          for (let x in errors) {
            errMsg.push(x + ' - ' + errors[x]);
            }
            modalProp.size='lg';
        }
        else{
         errMsg.push(err.response.data);
         modalProp.size='lg';
        }
          if(errMsg?.length>0){
            modalProp.content= errMsg;
            modalProp.isError=true;
            setModalMsg(modalProp)
            setShowModal(true);
          }
          setLoading(false);
      }
    );
  };

  const formularyValidation = (obj) => {
    let formError = {};
    formError["description"] = "";
    formError["defaultGenericFormularyStatus"] = "";
    formError["defaultBrandFormularyStatus"] = "";
    formError["defaultOTCFormularyStatus"] = "";
    formError["file"] = "";
    formError["isFormValid"] = true;
    let requiredFieldText = "This field is required";

    if (!obj.description) {
      formError.description = requiredFieldText;
      formError.isFormValid = false;
    }
    if (obj.description) {
      var isSpace = patten.test(obj.description[0])
      if (isSpace) {
        formError.description = "Description cannot begin with a space";
        formError.isFormValid = false;
      }
    }

    if (!obj.defaultGenericFormularyStatus) {
      formError.defaultGenericFormularyStatus = requiredFieldText;
      formError.isFormValid = false;
    }
    if (!obj.defaultBrandFormularyStatus) {
      formError.defaultBrandFormularyStatus = requiredFieldText;
      formError.isFormValid = false;
    }
    if (!obj.defaultOTCFormularyStatus) {
      formError.defaultOTCFormularyStatus = requiredFieldText;
      formError.isFormValid = false;
    }
    if (!obj.file && !id) {
      formError.file = requiredFieldText;
      formError.isFormValid = false;
    }
    return formError;
  };

  const handleClose = (redirectToHomePage) => {
      setShowModal(false);
      if(redirectToHomePage)
        navigate("/formularies");
  }

  const handleCheckboxChange = (event) => {
    const { name,checked } = event.target;
    setFormulary({ ...formulary, [name]: checked });
    if(!checked){
        formulary.file='';
        document.getElementById('file').value = null;
    }
  };

  const validate=()=>{
    if(prevStatus === formulary.isActive){
      formularyAdd();
    }else{
      let warningMsg =`You are about to ${formulary.isActive ? 'reactivate':'inactivate'} formulary. Are you sure you want to continue?`;
      setActiveInactiveWarningMsg(warningMsg);
      setShowWarningModal(true);
      return false;
    }
  }


  const handleYesClick=()=>{
    setShowWarningModal(false);
    formularyAdd();
  }

  const handleNoClick=()=>{
    setShowWarningModal(false);
  }

  const exportFormulary=async(e)=>{
    e.preventDefault();
     const config = { headers: { "content-type": "text/csv;charset=UTF-8" } };
    await apiClient().get(`/api/formularies/${id}/${e.target.id}`,config).then(
      (response) => {    
      JsonToCSVDownload(response.data,`formulary_${id}_version_${e.target.id}.csv`);    
      },(err)=>{
        modalProp.content = [];
        modalProp.content.push(err?.response?.data);
        modalProp.isError=true;
        setModalMsg(modalProp)
        setShowModal(true);        
      });
  }

  const validateCSV=()=>{
    navigate("/formularies/upload-result",{state:{file:formulary.file,id:id}});
  }

  return (
    <div className="container">
      <div className="row">
        <div className="col-12 py-3">
          <h1>In-House Formulary</h1> 
          {loading ? <LoadSpinner/>:''}           
          <div className={loading ? 'd-none':''}>
          <div className="form-group row input-required">
            <label htmlFor="description" className="col-sm-3 col-form-label">
              Description:
            </label>
            <div className="col-sm-4">
              <input
                type="text"
                className="form-control"
                id="description"
                name="description"
                data-testid="description"
                maxLength="50"
                minLength="1"
                value={formulary.description}
                onChange={handleInputChange}
                onKeyUp={onKeyUp}
              />
              {formError.description && (
                <span className="error" data-testid="descriptionErrorMsg">
                  {formError.description}
                </span>
              )}
            </div>
          </div>
          <div className="form-group row input-required">
            <label
              htmlFor="defaultGenericFormularyStatus"
              className="col-sm-3 col-form-label"
            >
              Default Generic Form Status:
            </label>
            <div className="col-sm-4">
              <select
                id="defaultGenericFormularyStatus"
                name="defaultGenericFormularyStatus"
                className="form-control"
                data-testid="defaultGenericFormularyStatus"
                value={formulary.defaultGenericFormularyStatus}
                onChange={handleInputChange}
              >
                <option key="gfEmpty" value="">
                  --Select--
                </option>
                {formularyStatus.map((data, index) => {
                  return (
                    <option key={data.key + `-gf`} value={data.key}>
                      {data?.value}
                    </option>
                  );
                })}
              </select>
              {formError.defaultGenericFormularyStatus && (
                <span
                  className="error"
                  data-testid="genericFormularyStatusErrorMsg"
                >
                  {formError.defaultGenericFormularyStatus}
                </span>
              )}
            </div>
          </div>
          <div className="form-group row input-required">
            <label
              htmlFor="defaultBrandFormularyStatus"
              className="col-sm-3 col-form-label"
            >
              Default Brand Form Status:
            </label>
            <div className="col-sm-4">
              <select
                id="defaultBrandFormularyStatus"
                name="defaultBrandFormularyStatus"
                data-testid="defaultBrandFormularyStatus"
                className="form-control"
                value={formulary.defaultBrandFormularyStatus}
                onChange={handleInputChange}
              >
                <option key="bfEmpty" value="">
                  --Select--
                </option>
                {formularyStatus.map((data, index) => {
                  return (
                    <option key={data.key + `-bf`} value={data.key}>
                      {data?.value}
                    </option>
                  );
                })}
              </select>
              {formError.defaultBrandFormularyStatus && (
                <span className="error" data-testid="brandFormularyErrorMsg">
                  {formError.defaultBrandFormularyStatus}
                </span>
              )}
            </div>
          </div>
          <div className="form-group row input-required">
            <label
              htmlFor="defaultOTCFormularyStatus"
              className="col-sm-3 col-form-label"
            >
              Default OTC Form Status:
            </label>
            <div className="col-sm-4">
              <select
                id="defaultOTCFormularyStatus"
                name="defaultOTCFormularyStatus"
                data-testid="defaultOTCFormularyStatus"
                className="form-control"
                value={formulary.defaultOTCFormularyStatus}
                onChange={handleInputChange}
              >
                <option key="ofEmpty" value="">
                  --Select--
                </option>
                {formularyStatus.map((data, index) => {
                  return (
                    <option key={data.key + `-of`} value={data.key}>
                      {data?.value}
                    </option>
                  );
                })}
              </select>
              {formError.defaultOTCFormularyStatus && (
                <span className="error" data-testid="otcFormularyErrorMsg">
                  {formError.defaultOTCFormularyStatus}
                </span>
              )}
            </div>
          </div>
          <div className={id ? 'form-group row':'form-group row input-required'}>
            <label htmlFor="file" className="col-sm-3 col-form-label">
              File Name:
            </label>
            <div className={(!formulary.isActive  && id > 0) ?`col-sm-4  is-disabled`:'col-sm-4'} data-testid="fileDiv">
              <div>
                <input
                  type="file"
                  data-testid="file"
                  accept=".csv"
                  name="file"
                  id="file"
                  onChange={changeHandler}
                />
              </div>
              {formError.file && (
                <span className="error" data-testid="fileErrorMsg">
                  {formError.file}
                </span>
              )}
            </div>
          </div>
          <div className="form-check">
              <div className="col-4 ml-n4 text-right">
                <input className="form-check-input"
                  id="isActive"
                  name="isActive"
                  type="checkbox"
                  data-testid="isActive"
                  onChange={handleCheckboxChange}                 
                  checked={formulary.isActive === true}
                />
                <label className="form-check-label" htmlFor="isActive">Active</label>
                {formulary.isActive}
              </div>
            </div>
            <div className="form-group row mt-2">
            <label htmlFor="file" className="col-sm-3 col-form-label"></label>
            <div className={ formulary.file ?'col-4':'col-4 is-disabled'}>
              <button
                  type="button"
                  data-testid="validateBtn"
                  className="btn btn-primary"
                  onClick={validateCSV}>
                  Validate
                </button>
            </div>
            </div>
          <div className="row">
            <div className="col-7">
              <div className="p-2 text-right">
                <button
                  type="button"
                  className="btn btn-cancel"
                  data-testid="backBtn"
                  onClick={() => navigate("/formularies")}
                >
                  Back
                </button>
                <button
                  type="button"
                  data-testid="saveBtn"
                  className="btn btn-primary"
                  onClick={validate}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
          {id && (
          <div className="row">
            <div className={formulary?.versions?.length >5 ?'innerscroll formulary-versions':''}>
              <div className="p-2">
              <div className="table-responsive">
                <table className="table table-striped table-bordered">
                  <thead>
                    <tr>
                      <th>Version</th>
                      <th>Date/Time</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formulary.versions?.length === 0 ? (
                      <tr>
                        <td className="no-data" colSpan="3">
                          <aside>No Data Found</aside>
                        </td>
                      </tr>
                    ) : (
                      <>
                        {formulary.versions?.map((val) => {
                          return (
                            <tr key={`version-key-${val.id}`}>
                              <td>{val.version}</td>
                              <td> <Moment format="MM/DD/YYYY h:mm:ss a">
                                 {val.createdOn}
                              </Moment></td>
                              <td><img alt={val.version} src="../../images/IconDownloadFlatFile.gif" id={val.version} onClick={exportFormulary}></img></td>
                            </tr>
                          );
                        })}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
              </div>
            </div>
          </div>
          )}
         </div>
          <SimpleModal showModal={showModal} modalProp={modalMsg} handleClose={handleClose}></SimpleModal>
          <YesNoModal showModal={showWarningModal} content={activeInactiveWarningMsg} handleYesClick={handleYesClick} handleNoClick={handleNoClick}></YesNoModal>
          <FormularyUploadProgress show={showProgressModal} progress={uploadProgress} />
        </div>
      </div>
    </div>
  );
}

export default FormularyAdd;
